import React from 'react';
import destionation from '@src/assets/destination.svg';
import { ZoneMapProps, VARIANT_SAFE_LAND } from './types';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFreehandShapeUponCreation,
  selectHoveredSafeLandZoneId,
  selectSelectedZone,
  selectZoneMapLocationUponCreation,
  setSelectedZone,
} from '@src/features/zones/redux';
import { getGeocode } from 'use-places-autocomplete';
import { DistanceLabel, setMapLocation } from '@src/core/controllers';
import { trackError } from '@src/core/sentry';
import { useGoogleMap } from '@react-google-maps/api';
import { useLocation } from 'react-router-dom';
import FreehandShapeDrawer from '@src/features/zones/components/free-hand-shape-drawer';
import ArcZonePolygon from '@src/core/ui/arc-zone-polygon';
import {
  selectDisplayNoFlyZones,
  selectDisplaySafeLandZones,
  selectHoveredNoFlyZoneId,
  ZoneShapeType,
} from '@src/core/features';
import ArcZoneMarker from '@src/core/ui/arc-zone-marker';
import { ArcSafeLand } from '@src/core/ui/arc-zafe-land';

const ZonesMap: React.FC<ZoneMapProps> = (props) => {
  const { zones } = props;

  const dispatch = useDispatch();
  const createdZoneUponCreation = useSelector(selectZoneMapLocationUponCreation);
  const freehandShapeUponCreation = useSelector(selectFreehandShapeUponCreation);
  const selectedZone = useSelector(selectSelectedZone);
  const hoveredSafeLandZoneId = useSelector(selectHoveredSafeLandZoneId);
  const displaySafeLandZones = useSelector(selectDisplaySafeLandZones);
  const displayNoFlyZones = useSelector(selectDisplayNoFlyZones);
  const hoveredNoFlyZoneId = useSelector(selectHoveredNoFlyZoneId);

  const location = useLocation();
  const isPolygonDrawStage = location.search.includes('stage=2');
  const freehandPolyN = freehandShapeUponCreation?.polygonCoordinates?.length ?? 0;

  const freehandPathForDistance = React.useMemo((): google.maps.LatLngLiteral[] => {
    const coords = freehandShapeUponCreation?.polygonCoordinates;
    if (!coords || coords.length < 2) return [];
    const mapped = coords.map((c) => ({
      lat: Number(c.lat),
      lng: Number(c.lng),
    }));
    if (mapped.length >= 3) {
      return [...mapped, mapped[0]];
    }
    return mapped;
  }, [freehandShapeUponCreation?.polygonCoordinates]);

  const showFreehandDistance = freehandPolyN >= 2 && (isPolygonDrawStage || freehandPolyN >= 3);

  const filteredActiveZones = zones.filter((zone) => zone.isActive);

  const map = useGoogleMap();

  const onDragDrop = async (e: google.maps.MapMouseEvent) => {
    const lat = e?.latLng?.lat();
    const lng = e?.latLng?.lng();

    if (!lat || !lng) return;

    try {
      const payload = { location: { lat, lng } };
      const addresses = await getGeocode(payload);
      const [{ formatted_address }] = addresses;

      dispatch(setMapLocation({
        latitude: lat,
        longitude: lng,
        address: formatted_address
      }));
    } catch (error: unknown) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        code: 'FE_0008',
        extra: { lat, lng, source: 'zonesMapDragDrop' },
      });
    }
  };
  

  return (
    <>
      {filteredActiveZones.map((zone) => {
        if (zone.id === Number(createdZoneUponCreation?.id)) return null;
        if(zone.id === Number(freehandShapeUponCreation?.id)) return null;

        const isSafeLand = zone.altitudeMeters != null;
        if (zone.shape === ZoneShapeType.CIRCLE && isSafeLand) {
          if (!displaySafeLandZones) return null;
          const safeLandHighlighted =
            selectedZone?.id === zone.id || hoveredSafeLandZoneId === zone.id;
          const SafeLandMarker = safeLandHighlighted ? ArcSafeLand.selected : ArcSafeLand.default;
          return (
            <SafeLandMarker
              key={zone.id}
              position={{
                lat: Number(zone.centerLatitude),
                lng: Number(zone.centerLongitude),
              }}
              onClick={() => dispatch(setSelectedZone(zone))}
            />
          );
        }
        if (!displayNoFlyZones) return null;
        const noFlyHighlight =
          hoveredNoFlyZoneId != null && Number(hoveredNoFlyZoneId) === Number(zone.id);
        return zone.shape === ZoneShapeType.CIRCLE ? (
          <ArcZoneMarker
            key={zone.id}
            position={{
              lat: Number(zone.centerLatitude),
              lng: Number(zone.centerLongitude),
            }}
            radius={zone.radiusMeters}
            icon={{
              url: destionation,
              scaledSize: new window.google.maps.Size(100, 50),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(17, 44),
            }}
            label={zone.name}
            type={zone.zoneType}
            showMarkerIcon={true}
            strokeHighlight={noFlyHighlight}
            onClick={() => { dispatch(setSelectedZone(zone));}}
          />
        ) : (
          <ArcZonePolygon
            key={zone.id}
            path={zone.polygonCoordinates as google.maps.LatLngLiteral[]}
            label={zone.name}
            type={zone.zoneType}
            showMarkerIcon={true}
            strokeHighlight={noFlyHighlight}
            onClick={() => { dispatch(setSelectedZone(zone));}}
          />
        );
      })}

  
      {createdZoneUponCreation && createdZoneUponCreation.variant === VARIANT_SAFE_LAND ? (
        <ArcSafeLand.default
          key={createdZoneUponCreation.id}
          position={{
            lat: createdZoneUponCreation.latitude,
            lng: createdZoneUponCreation.longitude,
          }}
          draggable={true}
          onDragEnd={onDragDrop}
        />
      ) : createdZoneUponCreation ? (
        <ArcZoneMarker
          key={createdZoneUponCreation.id}
          position={{
            lat: createdZoneUponCreation.latitude,
            lng: createdZoneUponCreation.longitude,
          }}
          radius={createdZoneUponCreation.radius}
          label={createdZoneUponCreation.zoneName}
          type={createdZoneUponCreation.type}
          draggable={true}
          onDragEnd={onDragDrop}
          showMarkerIcon={true}
        />
      ) : null}

      <FreehandShapeDrawer
        active={location.search.includes("stage=2")}
        onComplete={(coords) => console.log("Created zone:", coords)}
        map={map}
      />

      {isPolygonDrawStage &&
        freehandShapeUponCreation &&
        (freehandShapeUponCreation.polygonCoordinates?.length ?? 0) >= 1 && (
        <ArcZonePolygon
          showMarkerIcon
          showPolygonFill={false}
          path={freehandShapeUponCreation.polygonCoordinates as google.maps.LatLngLiteral[]}
          label={freehandShapeUponCreation.name}
          type={freehandShapeUponCreation.type}
        />
      )}

      {showFreehandDistance && freehandPathForDistance.length >= 2 && (
        <DistanceLabel path={freehandPathForDistance} />
      )}

      {freehandShapeUponCreation
        && !isPolygonDrawStage
        && (freehandShapeUponCreation.polygonCoordinates?.length ?? 0) >= 3 && (
        <ArcZonePolygon
          showMarkerIcon={true}
          path={freehandShapeUponCreation.polygonCoordinates}
          label={freehandShapeUponCreation.name}
          type={freehandShapeUponCreation.type}
        />
      )}
    </>
  );
};

export default ZonesMap;
